import React, { useEffect, useState } from 'react';
import { Form, Select, Avatar, Typography } from 'antd';
import { useCustomerSearch } from '../../../hooks/useCustomerSearch';

const { Option } = Select;
const { Text } = Typography;

const CustomerSelect = ({ form, isEdit }) => {
  const { customers, loading, searchTerm, isSearching, setSearchTerm } = useCustomerSearch();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Get the customer ID from the form values, handling both array and object formats
  const getCustomerId = () => {
    try {
      const formValues = form.getFieldsValue(true);
      
      // If customer_licenses is an array (new format)
      if (Array.isArray(formValues.customer)) {
        // Find the primary customer or take the first one
        const customerLicense = formValues.customer.find(
          cl => cl.is_primary
        ) || formValues.customer[0];
        
        // Return the customer_id if available
        if (customerLicense?.customer_id) {
          return customerLicense.customer_id;
        }
        
        // Fallback to direct customer ID if available
        if (customerLicense?.customer?.id) {
          return customerLicense.customer.id;
        }
      }
      
      // Fallback to direct customer_id field
      if (formValues.customer_id) {
        return formValues.customer_id;
      }
      
      // Fallback to old format (direct customer_licenses as ID)
      if (formValues.customer && !Array.isArray(formValues.customer)) {
        return formValues.customer;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting customer ID:', error);
      return null;
    }
  };
  
  const [customerId, setCustomerId] = useState(getCustomerId());
  
  // Watch for form changes and update customerId
  useEffect(() => {
    const newCustomerId = getCustomerId();
    if (newCustomerId !== customerId) {
      setCustomerId(newCustomerId);
    }
  }, [form]);
  
  // Enhanced debugging for customer ID resolution
  useEffect(() => {
    
    // // Log form values with more details
    // const formValues = form.getFieldsValue(true);
    
    // // Log the resolved customer from the list
    // if (customerId) {
    //   const foundCustomer = customers.find(c => c.id === customerId);
    // }
    
    // Add a debug function to test ID resolution
    const debugGetCustomerId = () => {
      try {
        const formValues = form.getFieldsValue(true);
        if (Array.isArray(formValues.customer)) {
          formValues.customer.forEach((cl, i) => {
            console.log(`    [${i}]:`, {
              customer_id: cl.customer_id,
              customer: cl.customer ? { id: cl.customer.id } : 'no customer',
              is_primary: cl.is_primary
            });
          });
        }
      } catch (e) {
        console.error('Debug error:', e);
      }
    };
    
    debugGetCustomerId();
    
  }, [customerId, customers, form]);
  // When in edit mode and customer_id changes, find and set the selected customer
  useEffect(() => {
    if (isEdit && customerId) {
      // If we already have the customer in the list, use it
      const foundCustomer = customers.find(c => c.id === customerId);
      if (foundCustomer) {
        setSelectedCustomer(foundCustomer);
      } else if (customerId) {
        // If customer is not in the list but we have an ID, create a minimal customer object
        setSelectedCustomer({
          id: customerId,
          company_name: 'Loading customer...',
          company_logo: null,
          status: 'active'
        });
      }
    }
  }, [customerId, customers, isEdit]);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // If in edit mode and we have a selected customer, show a display-only field
  if (isEdit && selectedCustomer) {
    const logoUrl = selectedCustomer.company_logo ? (
      selectedCustomer.company_logo.startsWith('uploads/') ? 
        `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}/static/${selectedCustomer.company_logo.replace('uploads/', '')}` :
        selectedCustomer.company_logo.startsWith('/static/') ?
          `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}${selectedCustomer.company_logo}` :
          selectedCustomer.company_logo
    ) : null;

    return (
      <Form.Item
        label="Customer"
        extra="Customer cannot be changed after license creation"
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          padding: '8px 11px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          backgroundColor: '#f5f5f5'
        }}>
          <Avatar 
            src={logoUrl}
            size="small"
            style={{ 
              backgroundColor: selectedCustomer.company_logo ? 'transparent' : '#1890ff',
              border: selectedCustomer.status === 'active' ? '1px solid #19f705' : '1px solid #fc0202',
              boxSizing: 'border-box',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {!selectedCustomer.company_logo && selectedCustomer.company_name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Text strong>{selectedCustomer.company_name}</Text>
        </div>
      </Form.Item>
    );
  }

  // In create mode, show the customer selection dropdown
  return (
    <Form.Item
      name="customer"
      label="Customer"
      rules={[{ required: true, message: 'Please select a customer' }]}
    >
      <Select
        showSearch
        placeholder="Search and select a customer"
        loading={loading}
        filterOption={false}
        onSearch={handleSearch}
        onFocus={() => setSearchTerm(searchTerm)}
        notFoundContent={isSearching ? 'Searching...' : 'No customers found'}
        optionLabelProp="label"
        popupRender={menu => (
          <div>
            <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                {customers.length} {customers.length === 1 ? 'customer' : 'customers'} found
              </div>
            </div>
            {menu}
          </div>
        )}
      >
        {customers.map(customer => {
          const logoUrl = customer.company_logo ? (
            customer.company_logo.startsWith('uploads/') ? 
              `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}/static/${customer.company_logo.replace('uploads/', '')}` :
              customer.company_logo.startsWith('/static/') ?
                `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}${customer.company_logo}` :
                customer.company_logo
          ) : null;
          
          return (
            <Option 
              key={customer.id} 
              value={customer.id}
              label={customer.company_name}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar 
                  src={logoUrl}
                  size="small"
                  style={{ 
                    backgroundColor: customer.company_logo ? 'transparent' : '#1890ff',
                    border: customer.status === 'active' ? '1px solid #19f705' : '1px solid #fc0202',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {!customer.company_logo && customer.company_name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {customer.company_name}
                </span>
              </div>
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );
};

export default CustomerSelect;
